import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { canAccessLegalizacao } from '../components/legalizacaoAccess';

interface AdminUser {
  email: string;
  name?: string;
  accessLevel?: 'admin' | 'user';
  token?: string;
}

interface AdminAuthContextType {
  user: AdminUser | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

function readSavedAdminUser(): AdminUser | null {
  const saved = sessionStorage.getItem('admin-user');
  if (!saved) return null;

  try {
    const parsed = JSON.parse(saved) as AdminUser;
    if ((!parsed.accessLevel || parsed.accessLevel !== 'admin') && !canAccessLegalizacao(parsed.email)) {
      sessionStorage.removeItem('admin-user');
      return null;
    }
    if (!parsed.token) {
      sessionStorage.removeItem('admin-user');
      return null;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem('admin-user');
    return null;
  }
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(readSavedAdminUser);

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/claro/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      
      const data = await response.json();
      
      if (data.success && data.user && data.token) {
        if (data.user.accessLevel !== 'admin' && !canAccessLegalizacao(data.user.email)) {
          return false;
        }
        
        const adminUser: AdminUser = {
          email: data.user.email,
          name: data.user.name,
          accessLevel: data.user.accessLevel,
          token: data.token,
        };
        setUser(adminUser);
        sessionStorage.setItem('admin-user', JSON.stringify(adminUser));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    sessionStorage.removeItem('admin-user');
  }, []);

  return (
    <AdminAuthContext.Provider value={{ user, token: user?.token ?? null, isAuthenticated: !!user?.token, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
}
